/** Custom Estimate editor — sections (work areas) with material / labor / sub-bid
 *  lines, supplier tags, variable markup (estimate-level + per-section override),
 *  internal costing breakdown, customer proposal PDF, store shopping list, and
 *  a purchase order per supplier. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABEL = { material: "Material", labor: "Labor", sub: "Sub Bid" };
const lineAmt = (ln) => (Number(ln.qty) || 0) * (Number(ln.unit_cost) || 0);

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export default function CustomEstimateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [est, setEst] = useState(null);
  const [deals, setDeals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let ignore = false;
    api.get(`/custom-estimates/${id}`).then((r) => { if (!ignore) setEst((prev) => prev ?? r.data); }).catch(() => toast.error("Estimate not found"));
    api.get("/deals").then((r) => { if (!ignore) setDeals((r.data || []).filter((d) => !d.maintenance_plan)); });
    return () => { ignore = true; };
  }, [id]);

  const patch = (p) => { setEst((e) => ({ ...e, ...p })); setDirty(true); };
  const patchSection = (sid, p) =>
    patch({ sections: est.sections.map((s) => (s.id === sid ? { ...s, ...p } : s)) });
  const patchLine = (sid, lid, p) =>
    patchSection(sid, { lines: est.sections.find((s) => s.id === sid).lines.map((l) => (l.id === lid ? { ...l, ...p } : l)) });

  const addSection = () => patch({ sections: [...(est.sections || []), { id: uid(), name: "", scope_notes: "", markup_pct: null, lines: [] }] });
  const removeSection = (sid) => patch({ sections: est.sections.filter((s) => s.id !== sid) });
  const addLine = (sid, kind) => {
    const base = { id: uid(), kind, description: "", supplier: "", qty: 1, unit: kind === "labor" ? "hrs" : kind === "sub" ? "bid" : "ea", unit_cost: 0 };
    patchSection(sid, { lines: [...est.sections.find((s) => s.id === sid).lines, base] });
  };
  const removeLine = (sid, lid) =>
    patchSection(sid, { lines: est.sections.find((s) => s.id === sid).lines.filter((l) => l.id !== lid) });

  const totals = useMemo(() => {
    if (!est) return null;
    let cost = 0, sell = 0;
    const bySupplier = {}, byKind = { material: 0, labor: 0, sub: 0 };
    const materialSuppliers = new Set();
    for (const s of est.sections || []) {
      let sc = 0;
      for (const l of s.lines || []) {
        const a = lineAmt(l);
        sc += a;
        byKind[l.kind] = (byKind[l.kind] || 0) + a;
        if ((l.supplier || "").trim()) {
          bySupplier[l.supplier.trim()] = (bySupplier[l.supplier.trim()] || 0) + a;
          if (l.kind === "material") materialSuppliers.add(l.supplier.trim());
        }
      }
      const mk = s.markup_pct === null || s.markup_pct === "" || s.markup_pct === undefined ? Number(est.markup_pct) || 0 : Number(s.markup_pct) || 0;
      cost += sc;
      sell += sc * (1 + mk / 100);
    }
    return { cost, sell, profit: sell - cost, bySupplier, byKind, materialSuppliers: [...materialSuppliers] };
  }, [est]);

  const save = async (showToast = true) => {
    setSaving(true);
    try {
      const body = { ...est, markup_pct: Number(est.markup_pct) || 0 };
      const r = await api.put(`/custom-estimates/${id}`, body);
      setEst(r.data);
      setDirty(false);
      if (showToast) toast.success("Estimate saved");
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async (path, label) => {
    if (dirty && !(await save(false))) return;
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      let detail = "";
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ }
      toast.error(detail || `Could not build ${label}`);
    }
  };

  const saveTemplate = async () => {
    if (dirty && !(await save(false))) return;
    const name = window.prompt("Template name:", est.title || "");
    if (!name) return;
    try {
      await api.post(`/custom-estimates/${id}/save-template`, { name });
      toast.success(`Saved as template “${name}”`);
    } catch {
      toast.error("Could not save template");
    }
  };

  const getSignLink = async () => {
    if (dirty && !(await save(false))) return;
    try {
      const r = await api.post(`/custom-estimates/${id}/sign-link`);
      try { await navigator.clipboard.writeText(r.data.sign_url); toast.success("Sign link copied to clipboard"); }
      catch { window.prompt("Customer sign link (copy it):", r.data.sign_url); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create sign link");
    }
  };

  const pushToDeal = async () => {
    if (dirty && !(await save(false))) return;
    try {
      const r = await api.post(`/custom-estimates/${id}/push-to-deal`);
      toast.success(`Pushed ${formatCurrency(r.data.sell_price)} to the deal (Option A)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link a deal first");
    }
  };

  if (!est || !totals) return <div className="text-zinc-400 text-sm">Loading…</div>;
  const suppliers = Object.keys(totals.bySupplier);

  return (
    <div data-testid="custom-estimate-editor">
      <button onClick={() => navigate("/custom-estimates")} className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-950 mb-2" data-testid="back-to-estimates">← All custom estimates</button>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Estimate Title</label>
          <input value={est.title} onChange={(e) => patch({ title: e.target.value })} placeholder="e.g., Office Remodel — 1860 N Washington"
            className="w-full h-10 px-3 border border-zinc-300 rounded-sm font-bold text-zinc-950" data-testid="estimate-title" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Linked Deal</label>
          <select value={est.deal_id || ""} onChange={(e) => patch({ deal_id: e.target.value })}
            className="h-10 px-2 border border-zinc-300 rounded-sm text-sm bg-white max-w-[240px]" data-testid="estimate-deal-select">
            <option value="">— none —</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Markup %</label>
          <input type="number" value={est.markup_pct} onChange={(e) => patch({ markup_pct: e.target.value })}
            className="h-10 w-24 px-2 border border-zinc-300 rounded-sm font-mono" data-testid="estimate-markup" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Status</label>
          <select value={est.status} onChange={(e) => patch({ status: e.target.value })}
            className="h-10 px-2 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="estimate-status">
            <option>Draft</option><option>Final</option><option>Signed</option>
          </select>
        </div>
        <button onClick={() => save()} disabled={saving} data-testid="save-estimate-btn"
          className="h-10 px-5 bg-blue-900 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-50">
          {saving ? "Saving…" : dirty ? "Save*" : "Save"}
        </button>
      </div>

      {est.signed_at && (
        <div className="mb-4 border border-emerald-300 bg-emerald-50 rounded-sm px-4 py-3 text-sm text-emerald-800 font-bold" data-testid="signed-banner">
          ✓ Signed by {est.signed_by_name} on {(est.signed_at || "").slice(0, 10)}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_300px] gap-5 items-start">
        <div className="space-y-5">
          {(est.sections || []).map((s, si) => (
            <div key={s.id} className="border border-zinc-200 rounded-sm bg-white" data-testid={`section-${si}`}>
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-zinc-100 bg-zinc-50">
                <input value={s.name} onChange={(e) => patchSection(s.id, { name: e.target.value })} placeholder={`Work area ${si + 1} — e.g., Kitchen, Roof Deck`}
                  className="flex-1 min-w-[200px] h-9 px-2 border border-zinc-300 rounded-sm font-bold text-sm" data-testid={`section-name-${si}`} />
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Markup override</label>
                <input type="number" placeholder={String(est.markup_pct)} value={s.markup_pct ?? ""}
                  onChange={(e) => patchSection(s.id, { markup_pct: e.target.value === "" ? null : Number(e.target.value) })}
                  className="h-9 w-20 px-2 border border-zinc-300 rounded-sm font-mono text-sm" data-testid={`section-markup-${si}`} />
                <button onClick={() => removeSection(s.id)} className="text-[10px] font-bold uppercase text-red-700 hover:underline" data-testid={`remove-section-${si}`}>Remove</button>
              </div>
              <div className="px-4 pt-3">
                <textarea value={s.scope_notes} onChange={(e) => patchSection(s.id, { scope_notes: e.target.value })} rows={2}
                  placeholder="Customer-facing scope description for this area (shows on the proposal)…"
                  className="w-full px-2 py-1.5 border border-zinc-200 rounded-sm text-sm text-zinc-700" data-testid={`section-notes-${si}`} />
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                    <th className="px-4 py-2">Kind</th><th className="px-2 py-2">Description</th><th className="px-2 py-2">Supplier / Sub</th>
                    <th className="px-2 py-2 text-right">Qty / Hrs</th><th className="px-2 py-2">Unit</th><th className="px-2 py-2 text-right">Unit Cost / Rate</th>
                    <th className="px-2 py-2 text-right">Cost</th><th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(s.lines || []).map((l, li) => (
                    <tr key={l.id} className="border-b border-zinc-50" data-testid={`line-${si}-${li}`}>
                      <td className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{KIND_LABEL[l.kind]}</td>
                      <td className="px-2 py-1.5"><input value={l.description} onChange={(e) => patchLine(s.id, l.id, { description: e.target.value })}
                        className="w-full h-8 px-2 border border-zinc-200 rounded-sm" data-testid={`line-desc-${si}-${li}`} /></td>
                      <td className="px-2 py-1.5"><input value={l.supplier} onChange={(e) => patchLine(s.id, l.id, { supplier: e.target.value })}
                        placeholder={l.kind === "sub" ? "Sub name" : l.kind === "labor" ? "—" : "Home Depot / Lowe's…"}
                        className="w-36 h-8 px-2 border border-zinc-200 rounded-sm" data-testid={`line-supplier-${si}-${li}`} /></td>
                      <td className="px-2 py-1.5"><input type="number" value={l.qty} onChange={(e) => patchLine(s.id, l.id, { qty: e.target.value === "" ? 0 : Number(e.target.value) })}
                        className="w-20 h-8 px-2 border border-zinc-200 rounded-sm font-mono text-right" data-testid={`line-qty-${si}-${li}`} /></td>
                      <td className="px-2 py-1.5"><input value={l.unit} onChange={(e) => patchLine(s.id, l.id, { unit: e.target.value })}
                        className="w-14 h-8 px-2 border border-zinc-200 rounded-sm" data-testid={`line-unit-${si}-${li}`} /></td>
                      <td className="px-2 py-1.5"><input type="number" value={l.unit_cost} onChange={(e) => patchLine(s.id, l.id, { unit_cost: e.target.value === "" ? 0 : Number(e.target.value) })}
                        className="w-24 h-8 px-2 border border-zinc-200 rounded-sm font-mono text-right" data-testid={`line-cost-${si}-${li}`} /></td>
                      <td className="px-2 py-1.5 font-mono text-right font-bold whitespace-nowrap">{formatCurrency(lineAmt(l))}</td>
                      <td className="px-2 py-1.5"><button onClick={() => removeLine(s.id, l.id)} className="text-red-600 text-xs font-bold" data-testid={`remove-line-${si}-${li}`}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                <button onClick={() => addLine(s.id, "material")} className="h-8 px-3 border border-zinc-300 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:border-zinc-950" data-testid={`add-material-${si}`}>+ Material</button>
                <button onClick={() => addLine(s.id, "labor")} className="h-8 px-3 border border-zinc-300 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:border-zinc-950" data-testid={`add-labor-${si}`}>+ Labor (hrs × rate)</button>
                <button onClick={() => addLine(s.id, "sub")} className="h-8 px-3 border border-zinc-300 rounded-sm text-[10px] font-bold uppercase tracking-wider hover:border-zinc-950" data-testid={`add-sub-${si}`}>+ Sub Bid</button>
              </div>
            </div>
          ))}
          <button onClick={addSection} data-testid="add-section-btn"
            className="w-full h-11 border-2 border-dashed border-zinc-300 rounded-sm text-xs font-bold uppercase tracking-wider text-zinc-500 hover:border-blue-900 hover:text-blue-900">
            + Add Work Area / Section
          </button>
        </div>

        <div className="border border-zinc-200 rounded-sm bg-white p-4 space-y-3 lg:sticky lg:top-4" data-testid="estimate-totals">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Internal Costing</div>
          <Row label="Materials" v={totals.byKind.material} />
          <Row label="Labor" v={totals.byKind.labor} />
          <Row label="Sub bids" v={totals.byKind.sub} />
          <div className="border-t border-zinc-200 pt-2">
            <Row label="Total cost" v={totals.cost} bold />
            <Row label={`Sell @ markup`} v={totals.sell} bold blue testId="estimate-sell" />
            <Row label="Est. profit" v={totals.profit} bold green testId="estimate-profit" />
          </div>
          {suppliers.length > 0 && (
            <div className="border-t border-zinc-200 pt-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Cost by supplier</div>
              {suppliers.map((sp) => <Row key={sp} label={sp} v={totals.bySupplier[sp]} />)}
            </div>
          )}
          <div className="border-t border-zinc-200 pt-3 space-y-2">
            <Btn onClick={() => openPdf(`/custom-estimates/${id}/proposal.pdf`, "proposal")} testId="proposal-pdf-btn">Customer Proposal PDF</Btn>
            <Btn onClick={() => openPdf(`/custom-estimates/${id}/shopping-list.pdf`, "shopping list")} testId="shopping-list-btn">Shopping List PDF</Btn>
            {totals.materialSuppliers.map((sp) => (
              <Btn key={sp} onClick={() => openPdf(`/custom-estimates/${id}/purchase-order.pdf?supplier=${encodeURIComponent(sp)}`, "PO")} testId={`po-btn-${sp}`}>
                PO — {sp}
              </Btn>
            ))}
            <Btn onClick={saveTemplate} testId="save-template-btn">Save as Template</Btn>
            <Btn onClick={getSignLink} testId="get-sign-link-btn">Get Customer Sign Link</Btn>
            <Btn onClick={pushToDeal} testId="push-to-deal-btn" primary>Push Price to Deal</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, v, bold, blue, green, testId }) => (
  <div className="flex justify-between text-sm" data-testid={testId}>
    <span className={`${bold ? "font-bold" : ""} text-zinc-600`}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold" : ""} ${blue ? "text-blue-900" : green ? "text-emerald-700" : "text-zinc-900"}`}>{formatCurrency(v)}</span>
  </div>
);

const Btn = ({ children, onClick, testId, primary }) => (
  <button onClick={onClick} data-testid={testId}
    className={`w-full h-9 px-3 text-[10px] font-bold uppercase tracking-wider rounded-sm ${primary ? "bg-blue-900 text-white hover:bg-blue-800" : "border border-zinc-300 hover:border-zinc-950"}`}>
    {children}
  </button>
);
